// Phase 11 — Habits route with check-in toggle.
import * as React from 'react';
import { useHabits, useHabitCheckin, useHabitUncheck } from '../../hooks/useHabits.js';
import { Card, CardContent } from '../../components/ui/Card.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';
import { Checkbox } from '../../components/ui/Checkbox.js';

export function Habits(): JSX.Element {
  React.useEffect(() => { document.title = 'DailyDeck — Habits'; }, []);
  const query = useHabits();
  const checkin = useHabitCheckin();
  const uncheck = useHabitUncheck();

  return (
    <div className="space-y-6" data-testid="habits-route">
      <header>
        <h1 className="text-title-1 font-semibold text-text-primary">Habits</h1>
        <p className="text-body text-text-secondary mt-1">Daily and weekly habit tracking.</p>
      </header>

      {query.isPending && <div className="rounded-lg border border-border-subtle bg-surface-raised"><SkeletonRow /><SkeletonRow /></div>}
      {query.isError && <Card><CardContent className="py-4"><p className="text-body-strong text-text-primary">Could not load habits</p><Button variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button></CardContent></Card>}
      {query.isSuccess && query.data.length === 0 && <EmptyState title="No habits yet" description="Add a habit to start tracking." />}
      {query.isSuccess && query.data.length > 0 && (
        <div className="space-y-2">
          {query.data.map((h) => (
            <Card key={h.id}>
              <CardContent className="flex items-center gap-4 py-4">
                <Checkbox
                  checked={h.checked_today}
                  disabled={checkin.isPending || uncheck.isPending}
                  onCheckedChange={(checked) => {
                    if (checked) checkin.mutate({ habit_id: h.id });
                    else uncheck.mutate({ habit_id: h.id });
                  }}
                  aria-label={`Mark "${h.name}" as ${h.checked_today ? 'incomplete' : 'complete'} for today`}
                />
                <div className="min-w-0 flex-1">
                  <span className={`text-body-strong ${h.checked_today ? 'text-text-muted line-through' : 'text-text-primary'}`}>{h.name}</span>
                  {h.description && <p className="text-caption text-text-secondary">{h.description}</p>}
                </div>
                <span className="text-caption text-text-muted">{h.frequency}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
